import { useMemo } from "react";

import { useSelector } from "react-redux";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { ButtonPlaceholderLoader } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import signalEventsService from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalEventsService";
import type { SignalEventType } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/types";
import type { SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import { selectIntentSignalSelectedTopics } from "app/pages/sales-intelligence/sub-modules/intent/store/signal/selectors/selectors";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import { selectSignalsGroupedByType } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

import SignalEventFilterButton from "../SignalEventFilterButton/SignalEventFilterButton";

type SignalEventListProps = {
    value: SignalEventType;
    signalEvents: readonly SignalEventDto[];
    onFilterChange: (value: SignalEventType) => void;
    isLoading?: boolean;
};

const SignalEventFilterContainer = (props: SignalEventListProps) => {
    const { value, signalEvents, onFilterChange, isLoading } = props;
    const signalsGroupedByType = useSelector(selectSignalsGroupedByType);
    const selectedTopics: readonly Topic[] = useSelector(selectIntentSignalSelectedTopics);

    const filterConfig = useMemo(() => {
        const config = signalEventsService.getSignalEventsFilterConfig(signalEvents, signalsGroupedByType);
        return signalEventsService.getSignalEventsByIntentTopics(config, selectedTopics);
    }, [signalEvents, signalsGroupedByType, selectedTopics]);

    return (
        <Flex
            padding="16px 30px"
            borderBottom={`1px solid ${colorsPalettes.carbon["50"]}`}
            background={` ${rgba(colorsPalettes.carbon["25"], 0.4)}`}
        >
            <Text
                fontVariant="report"
                color={colorsPalettes.carbon[400]}
                flex="none"
                lineHeight="32px"
            >
                Filter by:
            </Text>
            <Flex
                margin="0 0 0 16px"
                flexWrap="wrap"
                gap="8px"
            >
                {isLoading
                    ? Array.from({ length: 9 }).map((_, index) => (
                          <Box
                              key={index}
                              width="100px"
                              height="36px"
                          >
                              <ButtonPlaceholderLoader />
                          </Box>
                      ))
                    : filterConfig.map((filterItemConfig) => (
                          <SignalEventFilterButton
                              key={filterItemConfig.label}
                              config={filterItemConfig}
                              onClick={onFilterChange}
                              filterValue={value}
                          />
                      ))}
            </Flex>
        </Flex>
    );
};

export default SignalEventFilterContainer;
